import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';

import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import furoApi from '../api/FuroApi';
import {
  Attachment,
  ErrorResponse,
  InventoryTransportLetter,
  Partner,
  SaveInventoryTransportLetterRequest,
  SaveInventoryTransportLetterResponse
} from '../api/dtos/dtos';
import locales from '../locales';
import * as paths from '../paths';
import {ErrorAlert} from '../common/ErrorAlert';
import InventoryTransportLetterForm from './InventoryTransportLetterForm';
import {
  showDeleteConfirm,
  showDeleteSuccessNotification,
  showErrorModal,
  showSaveSuccessNotification,
  showSuccessModal
} from "../utils/dialogs";
import {Button} from "antd";
import ButtonBar from "../common/ButtonBar";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import FileUploadEditor from "../common/input/FileUploadEditor";

interface State {
  editable: boolean;
  letter?: InventoryTransportLetter;
  partners: Partner[];
  users: string[];
  error?: ErrorResponse;
}

class InventoryTransportLetterDetailsPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    editable: false,
    users: [],
    partners: [],
  };

  public componentDidMount() {
    this.loadTransportLetter();
    this.loadUsers();
  }

  public componentDidUpdate(prevProps: Readonly<RouteComponentProps<paths.IdPageUrlParams>>) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.loadTransportLetter();
    }
  }

  public render(): React.ReactNode {
    const {letter, users, partners, editable, error} = this.state;

    const action = letter?.id ? furoApi.inventoryTransportLetters.uploadAttachmentsUrl(letter.id) : undefined;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 16}}>
          <Button
            type="danger"
            icon="delete"
            onClick={this.onDeleteClicked}
          >{locales.delete}</Button>
          <Button
            type="primary"
            icon="edit"
            disabled={editable}
            onClick={this.onEditClicked}
          >{locales.edit}</Button>
        </ButtonBar>

        {letter && (
          <InventoryTransportLetterForm
            editable={editable}
            transportLetter={letter}
            users={users}
            onFormCancel={this.onCancel}
            onFormSubmit={this.onSave}
            partners={partners}
            onPartnerDelete={this.deletePartner}
            onPartnerSearch={this.searchPartner}
          />
        )}


        {letter && letter.id &&
            <>
                <SectionHeaderFormItem title={locales.attachments} style={{marginTop: 48}}/>
                <FileUploadEditor
                    disabled={editable}
                    itemId={letter.id}
                    action={action}
                    loadAttachment={this.loadAttachment}
                    deleteAttachment={this.deleteAttachment}
                />
            </>
        }

        <ErrorAlert style={{marginTop: 16}} error={error}/>
      </>
    );
  }

  private getIdParam = (): number => Number(this.props.match.params.id);

  private loadAttachment = (id: number): Promise<Attachment[]> => {
    return furoApi.inventoryTransportLetters.getAttachments(id);
  }

  private deleteAttachment = (id: number, attachmentId: number): Promise<void> => {
    return furoApi.inventoryTransportLetters.deleteAttachment(id, String(attachmentId));
  }

  private loadTransportLetter = () => {
    const promise = furoApi.inventoryTransportLetters.get(this.getIdParam())
      .then(this.onLoadSuccess)
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadUsers = () => {
    const promise = furoApi.users.getUsers()
      .then((users) => this.setState({users: users.map(u => u.userName).filter(Boolean) as string[]}))
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadSuccess = (letter: InventoryTransportLetter) => {
    this.setState({letter, error: undefined});
    setAppHeaderSubtitle(this.props, letter.transportLetterNumber || locales.transport_letters);
  };

  private onError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onCancel = () => {
    this.setState({editable: false});
  };

  private onEditClicked = () => {
    this.setState({editable: true});
  };

  private onDeleteClicked = () => {
    showDeleteConfirm(this.deleteTransportLetter);
  };

  private deleteTransportLetter = () => {
    const {letter} = this.state;
    if (!letter?.id) {
      return;
    }
    const promise = furoApi.inventoryTransportLetters.delete(letter.id!!)
      .then(() => {
        showDeleteSuccessNotification();
        this.props.history.goBack();
      })

      .catch((e) => {
        showErrorModal(e.details.message);
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };


  private searchPartner = (value: string) => {
    furoApi.incomingInvoices.searchPartners(value)
      .then((partners) => this.setState({partners}))
      .catch(this.onError);
  };

  private deletePartner = (id: number) => {
    furoApi.incomingInvoices.deletePartner(id)
      .then((_) => this.onPartnerDeleted(id))
      .catch(this.onError);
  };

  private onPartnerDeleted = (id: number) => {
    showDeleteSuccessNotification();
    const {partners} = this.state;
    const idx = partners.findIndex(p => p.id === id);
    if (idx > -1) {
      partners.splice(idx, 1);
      this.setState({partners});
    }
  };

  private onSave = (letter: SaveInventoryTransportLetterRequest) => {
    const promise = furoApi.inventoryTransportLetters.save(this.getIdParam(), letter)
      .then(this.onCreateSuccess)
      .catch((e) => {
        showErrorModal(e.details.message);
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onCreateSuccess = (response: SaveInventoryTransportLetterResponse) => {
    if (response.updatedStockItemInvoiceCount) {
      showSuccessModal(locales.transport_letter_save_success_with_updated_invoices(response.updatedStockItemInvoiceCount));
    } else {
      showSaveSuccessNotification();
    }
    this.setState({error: undefined, editable: false});
  };
}

export default withRouter(InventoryTransportLetterDetailsPage);




// WEBPACK FOOTER //
// ./src/inventory-transport-letters/InventoryTransportLetterDetailsPage.tsx